import { EventBus } from '@antification/core';

export interface GameStats {
  // Parkington stats
  parkingLotsCompleted: number;
  totalAcreage: number;
  totalSpots: number;
  treesRemoved: number;
  efficiencyRating: number;

  // Formicarium stats
  totalColonies: number;
  totalAntDeaths: number;
  namesGiven: number;
  bottomScreenViewTime: number; // ms
  bottomScreenViewCount: number;

  // Hidden stats (the meta-joke)
  timesTriedToSaveAnts: number;

  // Session stats
  currentSessionStart: number;
  totalPlayTime: number;
}

export interface EndGameStats {
  // Parkington summary
  totalArea: string;
  parkingSpots: number;
  treesRemoved: number;
  efficiencyRating: string;
  customerSatisfaction: string;
  environmentalImpact: string;

  // Formicarium summary (only shown if viewed)
  initialPopulation: number;
  finalPopulation: number;
  queenStatus: string;
  colonyAge: string;
  lastSurvivor: string;
  foragingTripsLifetime: number;
  causeOfDeath: string;
}

export class StatsTracker {
  private events: EventBus;
  private stats: GameStats;
  private currentGameViewed = false;
  private currentGameBottomTime = 0;

  constructor(events: EventBus) {
    this.events = events;

    // Load or initialize stats
    this.stats = this.load() || {
      parkingLotsCompleted: 0,
      totalAcreage: 0,
      totalSpots: 0,
      treesRemoved: 0,
      efficiencyRating: 0,
      totalColonies: 0,
      totalAntDeaths: 0,
      namesGiven: 0,
      bottomScreenViewTime: 0,
      bottomScreenViewCount: 0,
      timesTriedToSaveAnts: 0,
      currentSessionStart: Date.now(),
      totalPlayTime: 0,
    };

    this.setupListeners();
  }

  private setupListeners(): void {
    // Track bottom screen views
    this.events.on('BOTTOM_SCREEN_VIEWED', () => {
      if (!this.currentGameViewed) {
        this.currentGameViewed = true;
        this.stats.bottomScreenViewCount++;
      }
    });

    // Track ant naming
    this.events.on('ANT_NAMED', () => {
      this.stats.namesGiven++;
      this.save();
    });

    // Track ant deaths
    this.events.on('ANT_DIED', () => {
      this.stats.totalAntDeaths++;
    });

    // Track "trying to save ants" - increment on suspicious clicks
    // This is tracked elsewhere and called via incrementSaveAttempts()
  }

  // Call this when the player does something that suggests they're trying to save ants
  incrementSaveAttempts(): void {
    this.stats.timesTriedToSaveAnts++;
    // Don't save to localStorage immediately - let it accumulate
  }

  // Update view time when viewing bottom screen
  updateBottomViewTime(dt: number): void {
    this.currentGameBottomTime += dt;
    this.stats.bottomScreenViewTime += dt;
  }

  // Called when parking lot is completed
  recordCompletion(
    spots: number,
    trees: number,
    colonyDeaths: number
  ): void {
    this.stats.parkingLotsCompleted++;
    this.stats.totalSpots += spots;
    this.stats.treesRemoved += trees;
    this.stats.totalAcreage += 2.4; // Fixed lot size
    this.stats.totalColonies++;
    this.stats.totalAntDeaths += colonyDeaths;

    // Calculate average efficiency
    this.stats.efficiencyRating =
      (this.stats.efficiencyRating * (this.stats.parkingLotsCompleted - 1) + 94.7) /
      this.stats.parkingLotsCompleted;

    this.save();
  }

  // Generate end game stats
  getEndGameStats(
    colonyStats: {
      initialPopulation: number;
      finalPopulation: number;
      queenStatus: 'alive' | 'deceased';
      colonyAge: number;
      lastSurvivor: string;
      foragingTrips: number;
    },
    parkingStats: {
      spots: number;
      trees: number;
    }
  ): EndGameStats {
    return {
      // Parkington
      totalArea: '2.4 acres',
      parkingSpots: 847, // Always 847
      treesRemoved: parkingStats.trees,
      efficiencyRating: '94.7%',
      customerSatisfaction: '⭐⭐⭐⭐⭐',
      environmentalImpact: '✅ Assessed',

      // Formicarium
      initialPopulation: colonyStats.initialPopulation,
      finalPopulation: colonyStats.finalPopulation,
      queenStatus: colonyStats.queenStatus === 'alive'
        ? 'Surviving'
        : `Deceased (Day ${Math.floor(colonyStats.colonyAge / 3600)})`,
      colonyAge: this.formatColonyAge(colonyStats.colonyAge),
      lastSurvivor: colonyStats.lastSurvivor,
      foragingTripsLifetime: colonyStats.foragingTrips,
      causeOfDeath: colonyStats.finalPopulation === 0 ? 'Starvation' : 'N/A',
    };
  }

  private formatColonyAge(ticks: number): string {
    // Convert ticks to "years, days" format for flavor
    const days = Math.floor(ticks / 3600);
    const years = 12; // Colony was 12 years old at start
    return `${years} years, ${days} days`;
  }

  // Get career stats for display
  getCareerStats(): Record<string, string | number> {
    return {
      'Parking lots completed': this.stats.parkingLotsCompleted,
      'Total acreage developed': this.stats.totalAcreage.toFixed(1),
      'Total parking spots': this.stats.totalSpots.toLocaleString(),
      'Total ant colonies': this.stats.totalColonies,
      'Total ant deaths': this.stats.totalAntDeaths.toLocaleString(),
      'Names you gave': this.stats.namesGiven,
      'Times you watched the bottom screen': this.stats.bottomScreenViewCount,
      'Time spent watching (minutes)': Math.floor(this.stats.bottomScreenViewTime / 60000),
    };
  }

  // Get the hidden stat for those who look in the settings file
  getHiddenStats(): Record<string, number> {
    return {
      times_player_tried_to_save_ants: this.stats.timesTriedToSaveAnts,
    };
  }

  // Export all stats as JSON (for settings file discovery)
  exportToJson(): string {
    return JSON.stringify(
      {
        parkington_stats: {
          lots_completed: this.stats.parkingLotsCompleted,
          total_acreage: this.stats.totalAcreage,
          total_spots: this.stats.totalSpots,
          trees_removed: this.stats.treesRemoved,
          average_efficiency: this.stats.efficiencyRating,
        },
        formicarium_stats: {
          colonies_affected: this.stats.totalColonies,
          total_ant_deaths: this.stats.totalAntDeaths,
          names_given: this.stats.namesGiven,
          view_time_seconds: Math.floor(this.stats.bottomScreenViewTime / 1000),
          view_count: this.stats.bottomScreenViewCount,
        },
        hidden: {
          times_player_tried_to_save_ants: this.stats.timesTriedToSaveAnts,
        },
        meta: {
          total_play_time_seconds: Math.floor(this.stats.totalPlayTime / 1000),
          first_session: this.stats.currentSessionStart,
        },
      },
      null,
      2
    );
  }

  private save(): void {
    try {
      // Update play time
      this.stats.totalPlayTime += Date.now() - this.stats.currentSessionStart;
      this.stats.currentSessionStart = Date.now();

      localStorage.setItem('parking-lot-stats', JSON.stringify(this.stats));
    } catch (e) {
      console.warn('Could not save stats:', e);
    }
  }

  private load(): GameStats | null {
    try {
      const stored = localStorage.getItem('parking-lot-stats');
      if (stored) {
        const data = JSON.parse(stored) as GameStats;
        data.currentSessionStart = Date.now();
        return data;
      }
    } catch (e) {
      console.warn('Could not load stats:', e);
    }
    return null;
  }

  // Reset current game tracking for new game
  resetCurrentGame(): void {
    this.currentGameViewed = false;
    this.currentGameBottomTime = 0;
  }
}
